import { useAui, useAuiState } from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';

import { datasetQueryKey } from '#plugin-chat/lib/data-pipeline/resolver/dataset-query-key.ts';

interface RerunOptions {
  /**
   * Invalidate the producer dataset cache before re-running. Producer tools
   * cache their dataset under `datasetQueryKey(threadId, toolName, args)`;
   * without this, re-running with the same args returns the cached dataset
   * and the producer body never re-executes.
   */
  force?: boolean;
}

interface ToolEntry {
  execute?: (args: unknown, ctx: unknown) => Promise<unknown>;
}

/**
 * Returns a function that re-executes a registered tool call with the given
 * args and dumps input + output (plus the LLM response message and timing) to
 * the browser console for debugging.
 *
 * Goes through the same wrapped `execute` the AI SDK uses on the live path —
 * tagged-arg conversion, schema validation, dataset resolution, and structured
 * error wrapping all run identically. The chat history is **not** mutated;
 * this is a read-side debug affordance.
 *
 * Pass `{ force: true }` (e.g. Shift-click) to invalidate the producer
 * dataset cache first, forcing a producer tool to actually re-fetch instead
 * of returning the cached dataset.
 */
export function useRerunTool() {
  const aui = useAui();
  const queryClient = useQueryClient();
  const threadId = useAuiState(({ threadListItem }) => threadListItem.id);

  return async (toolName: string, args: Record<string, unknown>, opts?: RerunOptions) => {
    const tools = aui.modelContext().getModelContext().tools as Record<string, ToolEntry | undefined> | undefined;
    const entry = tools?.[toolName];

    if (!entry?.execute) {
      console.error(`[Rerun] Tool not registered: ${toolName}`);
      return undefined;
    }

    const label = `[Rerun] ${toolName}${opts?.force ? ' (force)' : ''}`;
    // biome-ignore lint/suspicious/noConsole: debug tool
    console.group(label);
    console.debug('args:', args);

    if (opts?.force) {
      await queryClient.invalidateQueries({ queryKey: datasetQueryKey(threadId, toolName, args) });
    }

    const start = performance.now();
    try {
      const controller = new AbortController();
      const result = await entry.execute(args, { abortSignal: controller.signal });
      const ms = (performance.now() - start).toFixed(0);
      console.debug(`result (${ms}ms):`, result);
      if (result && typeof result === 'object' && 'message' in result) {
        console.debug('message:', (result as { message?: unknown }).message);
      }
      return result;
    } catch (err) {
      console.error('threw:', err);
      return undefined;
    } finally {
      // biome-ignore lint/suspicious/noConsole: debug tool
      console.groupEnd();
    }
  };
}
